import { Navigate, useParams } from 'react-router';
import { setReferralCookie, isValidReferralCode } from '@/lib/referral-cookies';

/**
 * `/r/:code` vanity-link target. Root-level PUBLIC route (sibling of the authed
 * AppShell tree) so an unauthenticated referee clicking a referral link is NOT
 * bounced to /signIn before the cookie is dropped.
 *
 * Drops the host-only `?ref=` attribution cookie at click time, then redirects
 * to `/signUp?ref=<code>` so SignupPage can carry `refCode`/`refClickedAt` on
 * the register mutation (most-recent-wins). The cookie is the source of truth;
 * the `?ref=` query param is informational/legible only.
 */
export function RedirectToSignup() {
  const { code } = useParams<{ code: string }>();

  // Validate the `:code` (URL-safe, 1–32 chars) before doing anything with it.
  // A missing/oversized/garbage value is treated as ABSENT: no cookie is
  // dropped and the redirect omits the `?ref=` param entirely.
  const validCode = isValidReferralCode(code) ? code : undefined;

  // Drop the cookie synchronously during render — only for a well-formed code.
  if (validCode) {
    setReferralCookie(validCode);
  }

  const target = validCode ? `/signUp?ref=${encodeURIComponent(validCode)}` : '/signUp';
  return <Navigate to={target} replace />;
}
